function value (object, keys) {
  for (const key of keys) {
    if (object && object[key] !== undefined && object[key] !== null && object[key] !== '') return object[key]
  }
  return null
}

function decorationValues (entry) {
  const location = entry && entry.location ? entry.location : {}
  const technique = entry && entry.technique ? entry.technique : {}
  const artwork = entry && (entry.artwork || entry.decoration || entry.flow_image) ? (entry.artwork || entry.decoration || entry.flow_image) : {}
  return {
    artwork: value(entry, ['artwork_name', 'file_name']) || value(artwork, ['file_name', 'name', 'tape_name']),
    techniqueId: value(technique, ['technique_id']) || value(entry, ['technique_id']),
    techniqueInternalId: value(technique, ['internal_id']) || value(entry, ['technique_internal_id']),
    locationCode: value(location, ['location_code', 'code']) || value(entry, ['location_code']),
    locationId: value(location, ['location_id', 'id']) || value(entry, ['location_id']),
    locationInternalId: value(location, ['internal_id']) || value(entry, ['location_internal_id']),
    productClass: value(location, ['product_class']) || value(entry, ['product_class']),
    ptlId: value(entry, ['program_technique_location_id', 'ptl_id', 'configuration_id']) || value(location, ['program_technique_location_id', 'ptl_id']),
    ptlIdentity: value(entry, ['ptl_identity', 'configuration_key']) || value(location, ['ptl_identity']),
    resolutionStatus: value(entry, ['resolution_status']),
    resolutionSource: value(entry, ['resolution_source']),
    evaluatorPath: value(entry, ['evaluator_resolution_path'])
  }
}

function itemKey (item) {
  return String(item && (item.line_id || item.id || item.item_id || item.style_id) || '')
}

function findItem (items, groupItem) {
  const key = itemKey(groupItem)
  return (items || []).find(item => itemKey(item) === key || (
    String(item.section_id || item.section_reference || '') === String(groupItem.section_id || '') &&
    String(item.style_info && item.style_info.style_id || item.style_id || '') === String(groupItem.style_id || '')
  )) || null
}

function sameResolvedIdentity (first, second) {
  if (!first || !second) return false
  return String(first.techniqueId || '') === String(second.techniqueId || '') &&
    String(first.locationId || '') === String(second.locationId || '')
}

export function classifyPlacementDiagnostic ({ builder, submission, pricing, pdf, targetProductClass }) {
  if (!submission || !submission.techniqueId || !submission.locationId) return 'Missing'
  if (builder && builder.resolutionStatus && String(builder.resolutionStatus).indexOf('ambiguous') >= 0) return 'Ambiguous'
  if (builder && builder.resolutionStatus && String(builder.resolutionStatus).indexOf('duplicate') >= 0) return 'Ambiguous'
  if (submission.productClass && targetProductClass && String(submission.productClass) !== String(targetProductClass)) return 'Stale'
  if (builder && !sameResolvedIdentity(builder, submission)) return 'Mismatch between builder and Order Group'
  if ((pricing && !sameResolvedIdentity(pricing, submission)) || (pdf && !sameResolvedIdentity(pdf, submission))) return 'Mismatch between pricing and submission'
  return 'Resolved'
}

export function validProductClassSpecificDifference (rows) {
  if (!rows || rows.length < 2) return false
  const locationIds = new Set(rows.map(row => String(row.submission.locationId || '')))
  const productClasses = new Set(rows.map(row => String(row.targetProductClass || '')))
  const internalIds = new Set(rows.map(row => String(row.submission.locationInternalId || '')))
  return locationIds.size > 1 && productClasses.size > 1 && internalIds.size === 1 && !internalIds.has('')
}

export function buildPlacementDiagnostics ({ group, groupIndex, builder, submissionGroup, pricingGroup, evaluatorGroup, pdfForm }) {
  const sections = builder && builder.sections ? builder.sections : []
  const submissionItems = submissionGroup && submissionGroup.order_items ? submissionGroup.order_items : []
  const pricingItems = pricingGroup && pricingGroup.items ? pricingGroup.items : []
  const evaluatorItems = evaluatorGroup && evaluatorGroup.items ? evaluatorGroup.items : []
  const pdfItems = pdfForm && pdfForm.order_items ? pdfForm.order_items : []
  const rows = []

  ;((group && group.items) || []).forEach(groupItem => {
    const section = sections.find(candidate => String(candidate.id || '') === String(groupItem.section_id || '')) || {}
    const line = (section.style_lines || []).find(candidate => String(candidate.id || '') === String(groupItem.line_id || '')) || {}
    const targetProductClass = line.style_info && line.style_info.product_class || null
    const lineResolutions = line.bundle_resolutions || {}
    const productClassMap = section.bundle_resolutions_by_product_class && section.bundle_resolutions_by_product_class[targetProductClass]
    const builderPlacements = lineResolutions[section.bundle_id] || (productClassMap && productClassMap[section.bundle_id]) || []
    const submissionItem = findItem(submissionItems, groupItem) || {}
    const pricingItem = findItem(pricingItems, groupItem) || {}
    const evaluatorItem = findItem(evaluatorItems, groupItem) || {}
    const pdfItem = findItem(pdfItems, groupItem) || {}
    const count = Math.max(builderPlacements.length, (submissionItem.order_item_locations || []).length, (pricingItem.locations || []).length, (evaluatorItem.locations || []).length, (pdfItem.order_item_locations || []).length)
    const decorations = []
    for (let index = 0; index < count; index += 1) {
      const builderEntry = builderPlacements[index] || null
      const builderSource = builderEntry ? {
        ...decorationValues(builderEntry),
        techniqueId: value(builderEntry.source_technique_hint, ['technique_id']),
        techniqueInternalId: value(builderEntry.source_technique_hint, ['internal_id']),
        locationCode: value(builderEntry.source_location_hint, ['location_code']),
        locationId: value(builderEntry.source_location_hint, ['location_id']),
        locationInternalId: value(builderEntry.source_location_hint, ['internal_id'])
      } : null
      const builderResolved = builderEntry ? decorationValues(builderEntry) : null
      const submission = decorationValues((submissionItem.order_item_locations || [])[index])
      const pricing = decorationValues((pricingItem.locations || [])[index])
      const evaluator = decorationValues((evaluatorItem.locations || [])[index])
      const pdf = decorationValues((pdfItem.order_item_locations || [])[index])
      decorations.push({
        index,
        source: builderSource,
        builder: builderResolved,
        submission,
        pricing,
        evaluator,
        pdf,
        status: classifyPlacementDiagnostic({ builder: builderResolved, submission, pricing, pdf, targetProductClass })
      })
    }
    rows.push({
      groupNumber: groupIndex + 1,
      groupId: group.id || null,
      sectionId: groupItem.section_id || null,
      styleId: groupItem.style_id || value(line.style_info, ['style_id']),
      styleNumber: value(line.style_info, ['style_name', 'style_number']),
      styleDescription: groupItem.style_description || value(line.style_info, ['style_description']),
      programId: value(section.program_info, ['program_id']),
      programName: value(section.program_info, ['program_name']),
      targetProductClass,
      groupingKey: group.group_key || null,
      decorations
    })
  })
  return rows
}
